import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  class: "xmodal-wrapper",
  id: "search-page-filters"
}
const _hoisted_2 = { class: "xmodal-inner" }
const _hoisted_3 = { class: "xmodal" }
const _hoisted_4 = { class: "form-container" }
const _hoisted_5 = { class: "row sc-form" }
const _hoisted_6 = { class: "sc-form-field-wrapper" }
const _hoisted_7 = { class: "sc-form-field" }
const _hoisted_8 = ["value", "title"]
const _hoisted_9 = { label: "Name" }
const _hoisted_10 = ["value", "title"]
const _hoisted_11 = { label: "Email" }
const _hoisted_12 = ["value", "title"]
const _hoisted_13 = { class: "error-wrapper" }
const _hoisted_14 = { class: "sc-form-field-wrapper" }
const _hoisted_15 = { class: "sc-form-field" }
const _hoisted_16 = { class: "sc-radio-group" }
const _hoisted_17 = { class: "error-wrapper" }
const _hoisted_18 = { class: "sc-form-field-wrapper" }
const _hoisted_19 = { class: "sc-form-field" }
const _hoisted_20 = { class: "error-wrapper" }
const _hoisted_21 = { class: "row" }
const _hoisted_22 = { class: "column" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "form-buttons" }
const _hoisted_25 = ["disabled"]

import { useSearchPageStore } from "@/store/store/searchPageStore";
import { ref, computed, watch } from "vue";
import {
  Field as VeeField,
  Form as VeeForm,
  ErrorMessage as VeeErrorMessage,
  useForm,
} from "vee-validate";
import * as yup from "yup";
import { SearchTypes, Operators } from "@/types/advanced-search";
import { useRouter, useRoute } from "vue-router";
import functions from "@/use/functions.js";

interface SearchFilter {
  id: number;
  name: string;
  label?: string;
  description: string;
  category?: string;
  visible?: boolean;
}

interface SearchQuery {
  searchFilter?: SearchFilter;
  filterParameterId: number;
  searchType: number;
  searchTerms: string[];
  searchTerm: string;
  operationId: number;
}

interface SearchOperation {
  id: number;
  name: string;
  label?: string;
  description: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdvancedSearchFilter',
  emits: ["addQueries"],
  setup(__props, { expose: __expose, emit: __emit }) {

const route = useRoute();

const emits = __emit;

const searchPageStore = useSearchPageStore();

const allowMultipleQueries = ref<boolean>(false);
const submitButtonDisabled = ref<boolean>(false);
const showError = ref<boolean>(false);

const searchTypes = computed<Array<SearchFilter>>(() => [
  {
    id: SearchTypes.APPLICATION_ID,
    name: "applicationId",
    label: "Application ID",
    description: "Application ID (referenceId)",
    visible: true,
  },
  {
    id: SearchTypes.SSN,
    name: "ssn",
    label: "SSN",
    description: "SSN",
    visible: true,
  },
  {
    id: SearchTypes.CLUID,
    name: "cluid",
    label: "CLUID",
    description: "CLUID",
    visible: searchPageStore.enableSearchOnCLUID,
  },
  {
    id: SearchTypes.NAME,
    name: "ApplicantNamePartialMatch",
    label: "Applicant Name",
    description:
      "Primary Applicant or Co-Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.NAME_EXACT,
    name: "ApplicantNameExactMatch",
    label: "Applicant Name - Exact Match",
    description:
      "Primary Applicant or Co-Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_NAME,
    name: "primaryApplicantName",
    label: "Primary Applicant Name",
    description: "Primary Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_NAME_EXACT,
    name: "primaryApplicantNameExact",
    label: "Primary Applicant Name - Exact Match",
    description:
      "Primary Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.CO_APPLICANT_NAME,
    name: "coApplicantName",
    label: "Co-Applicant Name",
    description: "Co-Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.CO_APPLICANT_NAME_EXACT,
    name: "coApplicantNameExact",
    label: "Co-Applicant Name - Exact Match",
    description:
      "Co-Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.EMAIL,
    name: "ApplicantEmailPartial",
    label: "Email Address",
    description:
      "Primary Applicant or Co-Applicant Email Address contains search term",
    category: "Email",
    visible: true,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_EMAIL,
    name: "PrimaryApplicantEmailPartialMatch",
    label: "Primary Applicant Email",
    description: "Primary Applicant Email contains search term",
    category: "Email",
    visible: true,
  },
  {
    id: SearchTypes.CO_APPLICANT_EMAIL,
    name: "CoApplicantEmailPartialMatch",
    label: "Co-Applicant Email",
    description: "Co-Applicant Email contains search term",
    category: "Email",
    visible: true,
  },
]);

const operators = ref<Array<SearchOperation>>([
  {
    id: Operators.EXACT_MATCH as number,
    name: "ExactMatch",
    label: "Exact Match",
    description: "Exact Match",
  },
  {
    id: Operators.PARTIAL_MATCH as number,
    name: "PartialMatch",
    label: "Partial Match",
    description: "Partial Match",
  },
]);

const uncategorizedSearchTypes = computed(() =>
  searchTypes.value
    .filter(
      (type) =>
        type.category !== "Email" && type.category !== "Name" && type.visible,
    )
    .sort((a, b) => a.label!.localeCompare(b.label!)),
);

const nameSearchTypes = computed(() =>
  searchTypes.value.filter((type) => type.category === "Name" && type.visible),
);

const emailSearchTypes = computed(() =>
  searchTypes.value.filter((type) => type.category === "Email" && type.visible),
);

const queries = ref<Array<SearchQuery>>([]);

const newQuery = ref<SearchQuery>({
  searchType: 0, // the actual search type used in the search
  searchTerm: "",
  searchTerms: [],
  filterParameterId: 0, // what the user selects
  searchFilter: undefined,
  operationId: 0,
});

const validationSchema = yup.object({
  filterParameterId: yup
    .number()
    .min(1, "A filter parameter is required")
    .transform((value) => (isNaN(value) ? null : value))
    .required("A filter parameter is required"),
  operationId: yup
    .number()
    .min(1, "An operation is required")
    .required("An operation is required"),
  searchTerm: yup
    .string()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .required("A search term is required"),
  searchTerms: yup
    .array()
    .of(yup.string())
    .required("Search terms are required"),
  searchType: yup.number().required("A selection is required"),
});

const { values, setFieldValue, resetForm, errors, validate } = useForm({
  validationSchema,
  initialValues: newQuery.value,
});

function getSearchFilterById(id: number): SearchFilter | undefined {
  return searchTypes.value.find((filter) => filter.id === id);
}

function isOperatorDisabled(operatorId: number): boolean {
  // Disable Partial Match for SSN
  if (
    values.filterParameterId === SearchTypes.SSN &&
    operatorId === Operators.PARTIAL_MATCH
  ) {
    return true;
  }

  return false;
}

function handleFilterParamChange() {
  // if searching by SSN - require exact match
  if (values.filterParameterId === SearchTypes.SSN) {
    setFieldValue("operationId", Operators.EXACT_MATCH);
  }

  let id = values.filterParameterId;
  let filter = getSearchFilterById(id);

  setFieldValue("searchType", id);
  setFieldValue("searchFilter", filter);
}

function handleOperationChange(operationId: number) {
  // Check if the operation is exact match or partial match and adjust the search type accordingly
  setSearchType();
}

function setSearchType() {
  let id = values.filterParameterId;
  let filter = getSearchFilterById(id);

  switch (values.filterParameterId) {
    case SearchTypes.NAME: // Handle exact match
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.NAME_EXACT;
      }
      break;
    case SearchTypes.PRIMARY_APPLICANT_NAME:
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.PRIMARY_APPLICANT_NAME_EXACT;
      }
      break;
    case SearchTypes.CO_APPLICANT_NAME:
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.CO_APPLICANT_NAME_EXACT;
      }
      break;
    default:
      break;
  }

  filter = getSearchFilterById(id);

  setFieldValue("searchType", id);
  setFieldValue("searchFilter", filter);
}

function filterAndSearch() {
  submitButtonDisabled.value = true;
  showError.value = false;

  // Check if valid form input
  if (!values.filterParameterId || !values.operationId || !values.searchTerm) {
    submitButtonDisabled.value = false;
    showError.value = true;

    validate();
    return;
  }

  // Set the searchType based on the operation selected
  setSearchType();

  setFieldValue("searchTerm", values.searchTerm);
  setFieldValue("searchTerms", [values.searchTerm]);

  queries.value.push({ ...values } as SearchQuery);
  emits("addQueries", queries.value);

  // CLOSE MODAL / form Reset handled by parent component
}

function closeModal() {
  searchPageStore.showAdvancedSearchModal = false;
  submitButtonDisabled.value = false;
  showError.value = false;

  resetForm();
}

function removeRule(index: number) {
  if (index !== -1) {
    queries.value.splice(index, 1);
  }
}

function resetQueries() {
  queries.value = [];
}

function getCurrentSearchTerm() {
  return route.query.SearchTerms
    ? Array.isArray(route.query.SearchTerms)
      ? (route.query.SearchTerms as string[])[0]
      : String(route.query.SearchTerms)
    : "";
}

const searchTermsParam = computed<string[]>(() => {
  return JSON.parse(route.query.SearchTerms?.toString() || "[]") || [];
});

const searchTermParam = computed<string>(() => {
  return searchTermsParam.value.length > 0 ? searchTermsParam.value[0] : "";
});

const currentQueryFromUrl = computed(() => {
  const validQuery =
    route.query.SearchType !== undefined &&
    route.query.SearchTerms !== undefined &&
    route.query.SearchOperation !== undefined;
  if (!validQuery) {
    return null;
  }
  return {
    filterParameterId: Number(route.query.SearchType) || 0,
    searchType: Number(route.query.SearchType) || 0,
    searchTerm: getCurrentSearchTerm(),
    searchTerms: route.query.SearchTerms
      ? Array.isArray(route.query.SearchTerms)
        ? (route.query.SearchTerms as string[])
        : [String(route.query.SearchTerms)]
      : [],
    operationId: Number(route.query.SearchOperation) || 0,
    searchFilter: getSearchFilterById(Number(route.query.SearchType) || 0),
  };
});

// watch searchPageStore.showAdvancedSearchModal to reset queries when it is opened
watch(
  () => searchPageStore.showAdvancedSearchModal,
  (newValue) => {
    if (newValue) {
      resetQueries();
      resetForm();
      submitButtonDisabled.value = false;
    }
  },
);

// Watch changes to operation
watch(
  () => values.operationId,
  (newValue) => {
    handleOperationChange(newValue);
  },
);

watch(
  () => values.filterParameterId,
  () => {
    handleFilterParamChange();
  },
);

__expose({
  queries,
  resetQueries,
  closeModal,
  currentQueryFromUrl,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          class: "xmodal-close icon-x",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(searchPageStore).showAdvancedSearchModal = false))
        }),
        _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "xmodal-header" }, "Advanced Search", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _cache[2] || (_cache[2] = _createElementVNode("label", { for: "filterParameterId" }, [
                  _createTextVNode("Filter Parameters"),
                  _createElementVNode("i", { class: "required" }, "*")
                ], -1)),
                _createVNode(_unref(VeeField), {
                  as: "select",
                  id: "filterParameterId",
                  name: "filterParameterId",
                  class: _normalizeClass({
                    'is-invalid': _unref(errors).filterParameterId,
                  })
                }, {
                  default: _withCtx(() => [
                    _cache[1] || (_cache[1] = _createElementVNode("option", { value: undefined }, " ", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uncategorizedSearchTypes.value, (option) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: option.id,
                        value: option.id,
                        title: option.description
                      }, _toDisplayString(option.label), 9, _hoisted_8))
                    }), 128)),
                    _createElementVNode("optgroup", _hoisted_9, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nameSearchTypes.value, (option) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: option.id,
                          value: option.id,
                          title: option.description
                        }, _toDisplayString(option.label), 9, _hoisted_10))
                      }), 128))
                    ]),
                    _createElementVNode("optgroup", _hoisted_11, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(emailSearchTypes.value, (option) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: option.id,
                          value: option.id,
                          title: option.description
                        }, _toDisplayString(option.label), 9, _hoisted_12))
                      }), 128))
                    ])
                  ]),
                  _: 1
                }, 8, ["class"])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_unref(VeeErrorMessage), {
                  name: "filterParameterId",
                  class: "error-message"
                })
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(operators.value, (operation) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: _normalizeClass(["sc-radio", { disabled: isOperatorDisabled(operation.id) }]),
                      key: operation.id
                    }, [
                      _createVNode(_unref(VeeField), {
                        type: "radio",
                        name: "operationId",
                        value: operation.id,
                        disabled: isOperatorDisabled(operation.id),
                        required: "",
                        class: _normalizeClass({
                        'is-invalid': _unref(errors).operationId,
                      })
                      }, null, 8, ["value", "disabled", "class"]),
                      _createElementVNode("span", null, _toDisplayString(operation.label), 1)
                    ], 2))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createVNode(_unref(VeeErrorMessage), {
                    name: "operationId",
                    class: "error-message"
                  })
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _cache[3] || (_cache[3] = _createElementVNode("label", { for: "searchTerm" }, [
                  _createTextVNode("Search"),
                  _createElementVNode("i", { class: "required" }, "*")
                ], -1)),
                _createVNode(_unref(VeeField), {
                  type: "text",
                  id: "searchTerm",
                  name: "searchTerm",
                  class: _normalizeClass({
                    'is-invalid': _unref(errors).searchTerm,
                  })
                }, null, 8, ["class"])
              ]),
              _createElementVNode("div", _hoisted_20, [
                _createVNode(_unref(VeeErrorMessage), {
                  name: "searchTerm",
                  class: "error-message"
                })
              ])
            ])
          ]),
          _cache[4] || (_cache[4] = _createElementVNode("div", { class: "row sc-form" }, null, -1)),
          _withDirectives(_createElementVNode("div", null, [
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(queries.value, (query, index) => {
                return (_openBlock(), _createElementBlock("li", { key: index }, [
                  _createElementVNode("div", _hoisted_21, [
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("button", {
                        class: "delete-btn icon-x",
                        onClick: ($event: any) => (removeRule(index))
                      }, null, 8, _hoisted_23)
                    ]),
                    _createTextVNode(" " + _toDisplayString(query.searchFilter?.label) + ": " + _toDisplayString(query.searchTerm), 1)
                  ])
                ]))
              }), 128))
            ])
          ], 512), [
            [_vShow, allowMultipleQueries.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_24, [
          _createElementVNode("button", {
            disabled: submitButtonDisabled.value,
            class: "xedit",
            onClick: filterAndSearch
          }, " Apply Filter and Search ", 8, _hoisted_25)
        ])
      ])
    ])
  ]))
}
}

})